import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Lock } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '@/api/firebase';
import { useAuthStore } from '@/app/store/auth.store';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useCountdown } from '@/shared/hooks/use-countdown';
import {
  useChangePasswordMutation,
  useForcedChangePasswordMutation,
  useSendResetCodeMutation,
  useVerifyResetCodeMutation,
} from '@/features/auth/login/hooks/use-login-mutations';
import { ResetApiError } from '@/features/auth/login/types/login.types';
import { checkPassword } from '@/shared/utils/password';
import { ShcLogo } from '@/components/brand/ShcLogo';
import { AuthFooterOrganization } from '@/features/auth/institution/AuthFooterOrganization';

// Email format: local@domain.TLD where the TLD is >=2 ASCII letters (aligns the FE to the spec email rule).
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

type Step = 1 | 2 | 3 | 4;

interface FieldErrors {
  email?: string;
  code?: string;
  password?: string;
  confirm?: string;
}

export const AdminResetPasswordPage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);

  const isForcedMode = searchParams.get('mode') === 'forced';
  // PRD 00 §3.3.1 — forced mode requires an authenticated session whose user
  // has the must-change flag set. Otherwise fall back to the self-directed flow.
  const forcedReady = isForcedMode && isAuthenticated && mustChangePassword;

  const [step, setStep] = useState<Step>(forcedReady ? 3 : 1);
  const [email, setEmail] = useState('');
  // request-otp returns 204 (no expiresAt). The OTP TTL is BE-owned; we drive the on-screen
  // countdown + resend gating from a client-side 5-minute window opened on send/resend.
  const OTP_WINDOW_MS = 5 * 60 * 1000;
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // SHCT-570 — OTP_RATE_LIMITED is a Business Logic Error (error standard §7.1), so it must be
  // acknowledged in a modal rather than flashed as a toast: the user has to learn that the resend
  // was refused, not merely that "something went wrong".
  const [rateLimitedOpen, setRateLimitedOpen] = useState(false);

  const sendCodeMutation = useSendResetCodeMutation();
  const verifyCodeMutation = useVerifyResetCodeMutation();
  const changePasswordMutation = useChangePasswordMutation();
  const forcedChangeMutation = useForcedChangePasswordMutation();

  const secondsLeft = useCountdown(codeExpiresAt);
  const timerText = useMemo(() => {
    const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
    const ss = String(secondsLeft % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [secondsLeft]);

  // Derive expiry straight from the deadline so it's correct on every render. (A prior effect
  // mirrored this into fieldErrors off a stale `secondsLeft` the moment a fresh window opened —
  // showing "expired" while the timer was still counting, and never clearing it. Reading the
  // timestamp avoids that lag; the per-second re-render from useCountdown keeps it live.) A real
  // wrong/expired code from the server still surfaces via fieldErrors.code (precedence below).
  const codeExpired =
    step === 2 &&
    codeExpiresAt != null &&
    new Date(codeExpiresAt).getTime() <= Date.now();

  const clearFieldError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  };

  // ── Step 1 → 2 ──────────────────────────────────────────────
  const handleSendCode = () => {
    setFieldErrors({});
    const trimmed = email.trim();
    if (!trimmed) {
      setFieldErrors({ email: t('resetPassword.errors.emailRequired', { ns: 'auth' }) });
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setFieldErrors({ email: t('resetPassword.errors.emailInvalid', { ns: 'auth' }) });
      return;
    }
    sendCodeMutation.mutate(
      { email: trimmed },
      {
        // Anti-enumeration: request-otp always 204 (same whether or not the email is registered),
        // so we always advance to the code-entry step and never branch on "email not registered".
        onSuccess: () => {
          setCodeExpiresAt(new Date(Date.now() + OTP_WINDOW_MS).toISOString());
          setCode('');
          setStep(2);
        },
        onError: (error) => handleResetError(error),
      },
    );
  };

  // ── Step 2 — Resend ─────────────────────────────────────────
  const handleResend = () => {
    if (!email.trim()) return;
    setFieldErrors({});
    sendCodeMutation.mutate(
      { email: email.trim() },
      {
        onSuccess: () => {
          setCodeExpiresAt(new Date(Date.now() + OTP_WINDOW_MS).toISOString());
          setCode('');
          toast.info(t('resetPassword.step2.resendToast', { ns: 'auth' }));
        },
        onError: (error) => handleResetError(error),
      },
    );
  };

  // ── Step 2 → 3 ──────────────────────────────────────────────
  const handleVerifyCode = () => {
    setFieldErrors({});
    if (!code) {
      setFieldErrors({ code: t('resetPassword.errors.codeRequired', { ns: 'auth' }) });
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setFieldErrors({ code: t('resetPassword.errors.codeLength', { ns: 'auth' }) });
      return;
    }
    verifyCodeMutation.mutate(
      { email: email.trim(), code },
      {
        onSuccess: (result) => {
          setResetToken(result.resetToken);
          setCodeExpiresAt(null);
          setStep(3);
        },
        onError: (error) => handleResetError(error),
      },
    );
  };

  // ── Step 3 → 4 ──────────────────────────────────────────────
  const handleChangePassword = (event: FormEvent) => {
    event.preventDefault();
    setFieldErrors({});

    const pwErrorKey = validatePasswordKey(password);
    if (pwErrorKey) {
      setFieldErrors({ password: t(pwErrorKey, { ns: 'auth' }) });
      return;
    }
    if (password !== confirm) {
      setFieldErrors({ confirm: t('resetPassword.errors.passwordMismatch', { ns: 'auth' }) });
      return;
    }

    if (forcedReady) {
      forcedChangeMutation.mutate(
        { newPassword: password, confirmPassword: confirm },
        {
          onSuccess: () => {
            // Forced password change done → force logout (drop Firebase session) + redirect to /login.
            void signOut(auth);
            logout();
            navigate('/login', { replace: true });
            toast.success(t('resetPassword.completion.bodyForced', { ns: 'auth' }));
          },
          onError: (error) => handleResetError(error),
        },
      );
      return;
    }

    if (!resetToken) {
      setFieldErrors({ password: t('resetPassword.errors.tokenInvalid', { ns: 'auth' }) });
      return;
    }
    changePasswordMutation.mutate(
      { resetToken, newPassword: password, confirmPassword: confirm },
      {
        onSuccess: () => {
          setStep(4);
        },
        onError: (error) => handleResetError(error),
      },
    );
  };

  // `field` is gone on purpose (SHCT-570): every branch below targets an explicit field, and the only
  // caller-driven one was the System-Error default, which no longer writes inline at all.
  const handleResetError = (error: unknown) => {
    if (!(error instanceof ResetApiError)) {
      toast.error(t('resetPassword.errors.serverError', { ns: 'auth' }));
      return;
    }
    switch (error.code) {
      case 'EMAIL_INVALID':
        setFieldErrors({
          email: error.fields?.email ?? t('resetPassword.errors.emailInvalid', { ns: 'auth' }),
        });
        return;
      case 'EMAIL_NOT_REGISTERED':
        // PRD 00 §3.3 — reject unregistered emails; stay on step 1 with the spec message.
        setFieldErrors({
          email:
            error.fields?.email ?? t('resetPassword.errors.emailNotRegistered', { ns: 'auth' }),
        });
        return;
      case 'EMAIL_SEND_FAILED':
        toast.error(t('resetPassword.errors.serverError', { ns: 'auth' }));
        return;
      case 'OTP_INVALID':
        setFieldErrors({
          code: error.fields?.code ?? t('resetPassword.errors.codeInvalid', { ns: 'auth' }),
        });
        return;
      case 'OTP_EXPIRED':
        setFieldErrors({
          code: error.fields?.code ?? t('resetPassword.errors.codeExpired', { ns: 'auth' }),
        });
        return;
      case 'OTP_RATE_LIMITED':
        // SHCT-570 — BE PasswordResetService enforces a 60s per-email send cooldown (plus a
        // 5-per-hour cap, DH-PWRESET-RATE-001) on request-otp. Two things were wrong here:
        //   1. the copy said 인증번호가 일치하지 않습니다 — the code was never checked, the SEND was
        //      refused, so it accused the user of a mistake they did not make;
        //   2. it was toasted as a 서버 연결 problem, but a refused-by-policy request is a Business
        //      Logic Error → Modal 필수 (error standard §7.1).
        // The button is now gated on the countdown, so reaching this means the hourly cap, a flow
        // restart inside the cooldown, or a second tab — all states the user must acknowledge.
        setRateLimitedOpen(true);
        return;
      case 'RESET_TOKEN_INVALID':
        setFieldErrors({ password: t('resetPassword.errors.tokenInvalid', { ns: 'auth' }) });
        return;
      case 'PASSWORD_POLICY_VIOLATION':
        setFieldErrors({
          password:
            error.fields?.newPassword ??
            error.fields?.password ??
            t('resetPassword.errors.passwordLength', { ns: 'auth' }),
        });
        return;
      case 'PASSWORD_CONFIRM_MISMATCH':
        setFieldErrors({
          confirm:
            error.fields?.confirmPassword ??
            t('resetPassword.errors.passwordMismatch', { ns: 'auth' }),
        });
        return;
      case 'PASSWORD_REUSED':
        // SHCT-587 — one wire code, two flows with DIFFERENT spec'd copy, so the message is picked
        // by flow, not by code:
        //   · 강제 재설정 (mode=forced, temp password) — PRD 00 §3.3.1 조건 분기 defines
        //     "새 비밀번호가 임시 비밀번호와 동일" → the 이전(임시) copy. Kept.
        //   · 본인 주도 재설정 (forgot password)      — PRD 00 §3.3 does not define the reuse case
        //     at all; the customer supplied the copy on this ticket (comment 40115).
        // Previously both flows shared the 이전(임시) copy, so a user who never received a temp
        // password was told their new password matched one — the confusion Greg reported.
        // BE dependency: inside the forced flow, "one of the last 3" (not the temp password) also
        // arrives as PASSWORD_REUSED, and this branch will still show the 임시 copy. The FE cannot
        // tell them apart. BE can fix it without any FE change by sending `fields.newPassword` —
        // it is already preferred over the flow copy below.
        setFieldErrors({
          password:
            error.fields?.newPassword ??
            error.fields?.password ??
            t(
              forcedReady
                ? 'resetPassword.errors.passwordSameAsTemp'
                : 'resetPassword.errors.passwordRecentlyUsed',
              { ns: 'auth' },
            ),
        });
        return;
      case 'INTERNAL_ERROR':
      default:
        // SHCT-570 — this branch used to write `error.message` into the field error map, putting the
        // server's own exception string on screen (same leak class as SHCT-685). Two things were wrong:
        // the raw text, and the surface. A 5xx/unknown failure is a System Error, and error standard
        // §7.1 routes those to a Toast or Modal — never an inline field message, which is reserved for
        // Validation Errors the user can act on. Toast alone, with the defined copy.
        toast.error(t('resetPassword.errors.systemError', { ns: 'auth' }));
        return;
    }
  };

  const isSubmitting =
    sendCodeMutation.isPending ||
    verifyCodeMutation.isPending ||
    changePasswordMutation.isPending ||
    forcedChangeMutation.isPending;

  return (
    <main className="login-page">
      <div className="login-page__container" style={{ maxWidth: 520 }}>
        <div className="login-page__logo" style={{ cursor: 'default' }}>
          <div className="login-page__logo-icon">
            <ShcLogo />
          </div>
          <h1 className="login-page__logo-text">{t('brand.serviceName', { ns: 'labels' })}</h1>
        </div>
        <p className="login-page__subtitle">{t('resetPassword.title', { ns: 'auth' })}</p>

        {!forcedReady && (
          <div className="reset-page__steps">
            <StepBadge n={1} step={step} label={t('resetPassword.steps.emailVerification', { ns: 'auth' })} />
            <span className="reset-page__step-sep">—</span>
            <StepBadge n={2} step={step} label={t('resetPassword.steps.authentication', { ns: 'auth' })} />
            <span className="reset-page__step-sep">—</span>
            <StepBadge n={3} step={step} label={t('resetPassword.steps.newPassword', { ns: 'auth' })} />
          </div>
        )}

        <form className="reset-page__form" onSubmit={handleChangePassword} noValidate>
          {step === 1 && (
            <>
              <p className="reset-page__hint">
                {t('resetPassword.step1.hint', { ns: 'auth' })}
              </p>
              <div className="reset-page__field">
                <label className="login-page__label" htmlFor="resetEmail">
                  {t('resetPassword.step1.email', { ns: 'auth' })}
                </label>
                <div className="login-page__input-wrap">
                  <input
                    id="resetEmail"
                    type="email"
                    className={`login-page__input ${fieldErrors.email ? 'login-page__input--error' : ''}`}
                    placeholder={t('login.emailPlaceholder', { ns: 'auth' })}
                    value={email}
                    autoComplete="email"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearFieldError('email');
                    }}
                  />
                </div>
                {fieldErrors.email && <p className="login-page__error">{fieldErrors.email}</p>}
              </div>
              <button
                type="button"
                className="btn btn-primary reset-page__submit"
                onClick={handleSendCode}
                disabled={sendCodeMutation.isPending}
              >
                {sendCodeMutation.isPending ? (
                  <>
                    <Loader2 size={18} className="spin" />
                    {t('resetPassword.step1.sending', { ns: 'auth' })}
                  </>
                ) : (
                  t('resetPassword.step1.submit', { ns: 'auth' })
                )}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="reset-page__hint">
                {t('resetPassword.step2.hint', { ns: 'auth', email })}
                <br />
                {t('resetPassword.step2.hintLine2', { ns: 'auth' })}
              </p>
              <div className="reset-page__field">
                <label className="login-page__label" htmlFor="resetCode">
                  {t('resetPassword.step2.codeLabel', { ns: 'auth' })}
                </label>
                <div className="reset-page__verify-row">
                  <div className="login-page__input-wrap" style={{ flex: 1 }}>
                    <input
                      id="resetCode"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className={`login-page__input ${fieldErrors.code || codeExpired ? 'login-page__input--error' : ''}`}
                      style={{ paddingLeft: 12 }}
                      placeholder={t('resetPassword.step2.codePlaceholder', { ns: 'auth' })}
                      value={code}
                      onChange={(e) => {
                        setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                        clearFieldError('code');
                      }}
                    />
                  </div>
                  <span className="reset-page__timer">{timerText}</span>
                  {/* SHCT-570 — the resend button is never gated on the countdown. The approved
                      prototype (reset-password.html #resetResendBtn) fires resend unconditionally
                      on click, with no timer check, and the BE send-rate limit this gate once
                      guarded against (a 60s cooldown + hourly cap) was removed by this same ticket.
                      Only an in-flight request disables the button. */}
                  <button
                    type="button"
                    className="btn btn-default btn-secondary"
                    onClick={handleResend}
                    disabled={sendCodeMutation.isPending}
                  >
                    {t('resetPassword.step2.resend', { ns: 'auth' })}
                  </button>
                </div>
                {fieldErrors.code ? (
                  <p className="login-page__error">{fieldErrors.code}</p>
                ) : codeExpired ? (
                  <p className="login-page__error">
                    {t('resetPassword.errors.codeExpired', { ns: 'auth' })}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="btn btn-primary reset-page__submit"
                onClick={handleVerifyCode}
                disabled={verifyCodeMutation.isPending || secondsLeft === 0}
              >
                {verifyCodeMutation.isPending ? (
                  <>
                    <Loader2 size={18} className="spin" />
                    {t('resetPassword.step2.verifying', { ns: 'auth' })}
                  </>
                ) : (
                  t('resetPassword.step2.submit', { ns: 'auth' })
                )}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              {forcedReady && (
                <div className="reset-page__forced-banner" role="alert">
                  {t('resetPassword.forced.banner', { ns: 'auth' })}
                </div>
              )}
              {!forcedReady && (
                <p className="reset-page__hint">
                  {t('resetPassword.step3.hint', { ns: 'auth' })}
                </p>
              )}
              <div className="reset-page__field">
                <label className="login-page__label" htmlFor="resetNewPassword">
                  {t('resetPassword.step3.newPassword', { ns: 'auth' })}
                </label>
                <div className="login-page__input-wrap">
                  <Lock className="login-page__input-icon" size={20} />
                  <input
                    id="resetNewPassword"
                    type="password"
                    className={`login-page__input ${fieldErrors.password ? 'login-page__input--error' : ''}`}
                    placeholder={t('resetPassword.step3.newPasswordPlaceholder', { ns: 'auth' })}
                    autoComplete="new-password"
                    maxLength={64}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearFieldError('password');
                    }}
                  />
                </div>
                {fieldErrors.password && <p className="login-page__error">{fieldErrors.password}</p>}
              </div>
              <div className="reset-page__field">
                <label className="login-page__label" htmlFor="resetConfirmPassword">
                  {t('resetPassword.step3.confirmPassword', { ns: 'auth' })}
                </label>
                <div className="login-page__input-wrap">
                  <Lock className="login-page__input-icon" size={20} />
                  <input
                    id="resetConfirmPassword"
                    type="password"
                    className={`login-page__input ${fieldErrors.confirm ? 'login-page__input--error' : ''}`}
                    placeholder={t('resetPassword.step3.confirmPasswordPlaceholder', { ns: 'auth' })}
                    autoComplete="new-password"
                    maxLength={64}
                    value={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value);
                      clearFieldError('confirm');
                    }}
                  />
                </div>
                {fieldErrors.confirm && <p className="login-page__error">{fieldErrors.confirm}</p>}
              </div>
              <button
                type="submit"
                className="btn btn-primary reset-page__submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="spin" />
                    {forcedReady
                      ? t('resetPassword.forced.submitting', { ns: 'auth' })
                      : t('resetPassword.step3.submitting', { ns: 'auth' })}
                  </>
                ) : forcedReady ? (
                  t('resetPassword.forced.submit', { ns: 'auth' })
                ) : (
                  t('resetPassword.step3.submit', { ns: 'auth' })
                )}
              </button>
            </>
          )}

          {step === 4 && (
            <div className="reset-page__success">
              <CheckCircle2 size={56} className="reset-page__success-icon" />
              <h2 className="reset-page__success-title">
                {t('resetPassword.completion.title', { ns: 'auth' })}
              </h2>
              <p className="reset-page__success-body">
                {t('resetPassword.completion.body', { ns: 'auth' })}
              </p>
              <button
                type="button"
                className="btn btn-primary reset-page__submit"
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('resetPassword.completion.action', { ns: 'auth' })}
              </button>
            </div>
          )}
        </form>

        {/* PRD 00 §3.3.1 — forced reset must not offer a Back/Cancel route. */}
        {!forcedReady && step !== 4 && (
          <div className="reset-page__links">
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: 'inherit' }}
            >
              {t('resetPassword.backToLogin', { ns: 'auth' })}
            </button>
          </div>
        )}
      </div>

      <footer className="login-page__footer">
        {/* Organization details come from GET /admin/institution (V129), not from the label catalog. */}
        <AuthFooterOrganization />
      </footer>

      {/* SHCT-570 — refused-by-policy resend. Modal, not toast (error standard §7.1). */}
      <InfoModal
        open={rateLimitedOpen}
        title={t('resetPassword.errors.rateLimitedTitle', { ns: 'auth' })}
        body={t('resetPassword.errors.rateLimited', { ns: 'auth' })}
        onClose={() => setRateLimitedOpen(false)}
      />
    </main>
  );
};

// Maps the canonical policy violation (PRD 21 §5.2) to the granular i18n key
// this screen shows per rule. Returns null when the password is valid.
function validatePasswordKey(value: string): string | null {
  switch (checkPassword(value)) {
    case 'tooShort':
      return 'resetPassword.errors.passwordLength';
    case 'tooLong':
      return 'resetPassword.errors.passwordMaxLength';
    case 'missingLetter':
      return 'resetPassword.errors.passwordMissingLetter';
    case 'missingNumber':
      return 'resetPassword.errors.passwordMissingNumber';
    case 'missingSymbol':
      return 'resetPassword.errors.passwordMissingSymbol';
    default:
      return null;
  }
}

interface StepBadgeProps {
  n: 1 | 2 | 3;
  step: Step;
  label: string;
}

function StepBadge({ n, step, label }: StepBadgeProps) {
  const classes = ['reset-page__step'];
  if (step === n) classes.push('is-active');
  else if (step > n) classes.push('is-done');
  return (
    <span className={classes.join(' ')}>
      <span className="reset-page__step-num">{n}</span>
      {label}
    </span>
  );
}

export default AdminResetPasswordPage;
